import * as React from 'react';
import type { AgentContinuation, AgentWorkCard as WorkCard } from '@cdf/api-client-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export type WorkCardValues = Record<string, string>;
export function workCardKey(card: WorkCard) { return card.actionId + ':' + card.revision; }
export function workCardValues(card: WorkCard): WorkCardValues {
  return Object.fromEntries(card.fields.flatMap(field => field.value === undefined ? [] : [[field.name, field.value]]));
}
export function workCardChanged(card: WorkCard, values: WorkCardValues) {
  return card.fields.some(field => (values[field.name] ?? '') !== (field.value ?? ''));
}
interface AgentWorkCardProps {
  choice: AgentContinuation;
  card: WorkCard;
  values: WorkCardValues;
  disabled: boolean;
  onChange: (values: WorkCardValues) => void;
  onSubmit: (mode: 'details' | 'review' | 'cancel', fields: WorkCardValues, native?: boolean) => void;
  onScreen: () => void;
}
const stateLabels: Record<WorkCard['state'], string> = {
  needs_details: 'Add details', ready_for_review: 'Ready for review', awaiting_review: 'Review prepared',
  pending: 'Save pending', uncertain: 'Check save outcome', completed: 'Completed', failed: 'Save failed',
  cancelled: 'Cancelled', stale: 'Review changed', expired: 'Expired', native_only: 'Continue on screen', read_only: 'Read only'
};

export function useAgentDeadline(expiresAt: string) {
  const [expired, setExpired] = React.useState(() => Date.parse(expiresAt) <= Date.now());
  React.useEffect(() => {
    const delay = Date.parse(expiresAt) - Date.now();
    setExpired(delay <= 0);
    if (delay <= 0) return;
    const timer = window.setTimeout(() => setExpired(true), Math.min(delay, 2_147_483_647));
    return () => window.clearTimeout(timer);
  }, [expiresAt]);
  return expired;
}

/** Presentation only: submitted values use the existing private continuation contract. */
export function AgentWorkCard({ choice, card, values, disabled, onChange, onSubmit, onScreen }: AgentWorkCardProps) {
  const id = React.useId();
  const root = React.useRef<HTMLElement>(null);
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const expired = useAgentDeadline(card.expiresAt);
  React.useEffect(() => {
    if (document.activeElement === document.body) root.current?.focus();
  }, []);
  const dirty = workCardChanged(card, values);
  const unresolved = card.state === 'pending' || card.state === 'uncertain';
  const editable = !expired && card.availableActions.includes('details');
  const state = expired && !card.execution && !card.proposal?.executionId ? 'expired' : card.state;
  function submit(mode: 'details' | 'review', native = false) {
    const next: Record<string, string> = {};
    for (const field of card.fields) {
      const value = values[field.name];
      if (mode === 'review' && field.required && !value?.trim()) next[field.name] = field.label + ' is required.';
      else if (value !== undefined && field.minLength !== undefined && value.length < field.minLength)
        next[field.name] = field.label + ' needs at least ' + field.minLength + ' characters.';
      else if (value !== undefined && field.maxLength !== undefined && value.length > field.maxLength)
        next[field.name] = field.label + ' must be at most ' + field.maxLength + ' characters.';
      else if (value && field.control === 'select' && !field.options.includes(value)) next[field.name] = 'Choose an available option.';
    }
    setErrors(next);
    if (Object.keys(next).length) {
      queueMicrotask(() => root.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus());
      return;
    }
    onSubmit(mode, values, native);
  }
  return <section ref={root} tabIndex={-1} aria-labelledby={id + '-heading'} className="mt-3 min-w-0 space-y-3 rounded-lg border bg-background p-3 text-xs">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <h3 id={id + '-heading'} className="font-semibold">{card.title}</h3>
      <Badge variant="outline">{stateLabels[state]}</Badge>
    </div>
    <ol aria-label="Work progress" className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
      {['Edit details', 'Review exact change', 'Confirmed result'].map((step, index) => <li key={step}
        aria-current={(card.state === 'completed' ? 2 : card.proposal && !dirty ? 1 : 0) === index ? 'step' : undefined}>
        {index + 1}. {step}
      </li>)}
    </ol>
    {choice.explanation && <p>{choice.explanation}</p>}
    {card.fields.length > 0 && <fieldset disabled={disabled || !editable} className="min-w-0 space-y-3">
      <legend className="mb-2 font-medium">Draft details</legend>
      {card.fields.map(field => {
        const fieldId = id + '-field-' + field.name;
        const error = errors[field.name];
        const common = { id: fieldId, 'aria-invalid': Boolean(error), 'aria-describedby': error ? fieldId + '-error' : undefined };
        const change = (value: string) => { onChange({ ...values, [field.name]: value }); setErrors(current => ({ ...current, [field.name]: '' })); };
        return <div key={field.name} className="min-w-0 space-y-1.5">
          <Label htmlFor={fieldId}>{field.label}{field.required ? ' (required)' : ' (optional)'}</Label>
          {field.control === 'select' ? <Select value={values[field.name] ?? ''} onValueChange={change} disabled={disabled || !editable}>
            <SelectTrigger {...common} className="w-full min-w-0"><SelectValue placeholder="Choose an option" /></SelectTrigger>
            <SelectContent>{field.options.map(option => <SelectItem key={option} value={option}>{option.replaceAll('_', ' ')}</SelectItem>)}</SelectContent>
          </Select> : field.control === 'multiline' ? <Textarea {...common} rows={4} className="min-w-0 resize-y" value={values[field.name] ?? ''} maxLength={field.maxLength}
            onChange={event => change(event.target.value)} /> : <Input {...common} value={values[field.name] ?? ''} maxLength={field.maxLength} onChange={event => change(event.target.value)} />}
          {error && <p id={fieldId + '-error'} role="alert" className="text-destructive">{error}</p>}
        </div>;
      })}
    </fieldset>}
    {dirty && <p role="status">Edits are kept here. Prepare a fresh exact review before confirming a save.</p>}
    {state === 'expired' || state === 'stale' ? <p role="status">This review is no longer current. Continue on screen or ask for a new action.</p> : null}
    {unresolved && <p role="status">Check the original save outcome below. Nothing will be submitted again.</p>}
    {card.evidence.length > 0 && <details>
      <summary className="cursor-pointer font-medium">Evidence ({card.evidence.length})</summary>
      <ul className="mt-2 space-y-1">{card.evidence.map(source => <li key={source.id + ':' + source.citation}>
        [{source.citation}] {source.sourceIdentity} · {source.location}{source.freshness ? ' · ' + new Date(source.freshness).toLocaleString() : ''}
      </li>)}</ul>
    </details>}
    {card.fields.length > 0 && <details><summary className="cursor-pointer font-medium">Draft preview</summary>
      <dl className="mt-2 space-y-2">{card.fields.map(field => <div key={field.name}>
        <dt className="font-medium">{field.label}</dt><dd className="whitespace-pre-wrap break-words">{values[field.name] || 'Not supplied'}</dd>
      </div>)}</dl>
    </details>}
    <div className="flex flex-wrap gap-2">
      {editable && <Button type="button" size="sm" variant="outline" disabled={disabled || !dirty} onClick={() => submit('details')}>Keep details</Button>}
      {editable && <Button type="button" size="sm" disabled={disabled} onClick={() => submit('review')}>Prepare exact review</Button>}
      {card.availableActions.includes('open_native') && !unresolved && <Button type="button" size="sm" variant="outline" disabled={disabled}
        onClick={() => dirty && editable ? submit('details', true) : onScreen()}>{card.state === 'completed' ? 'Open saved result' : dirty && editable ? 'Keep edits and continue on screen' : 'Continue on screen'}</Button>}
      {card.availableActions.includes('cancel') && <Button type="button" size="sm" variant="ghost" disabled={disabled} onClick={() => onSubmit('cancel', {})}>Cancel action</Button>}
    </div>
    <p className="text-muted-foreground">{card.fields.length ? 'Editing and preparing a review do not save, send or publish. Confirm the exact change separately.' : 'Select the exact record and follow its existing review on the native screen. Opening it does not execute this action.'}</p>
  </section>;
}
